import { useCallback, useEffect, useRef, useState } from 'react';
import { leadpagesAPI } from '../services/api';
import '../pages/Leadpages.css';

export const formatLabel = (key) => (key || '').replaceAll('_', ' ');

function LeadpagePreviewModal({ page, html, onClose, onToggleTake, onPageUpdate }) {
    const [device, setDevice] = useState('desktop');
    const [iframeHtml, setIframeHtml] = useState(html);
    const [versions, setVersions] = useState([]);
    const [viewingId, setViewingId] = useState(null); // null = active version
    const [editText, setEditText] = useState('');
    const [editing, setEditing] = useState(page.edit_in_progress);
    const [editError, setEditError] = useState(null);
    const editPollRef = useRef(null);

    const loadVersions = useCallback(async () => {
        try {
            const res = await leadpagesAPI.getVersions(page.id);
            setVersions(res.data);
        } catch { /* versions are best-effort */ }
    }, [page.id]);

    useEffect(() => {
        loadVersions();
        const onKey = (e) => e.key === 'Escape' && onClose();
        window.addEventListener('keydown', onKey);
        return () => {
            window.removeEventListener('keydown', onKey);
            clearInterval(editPollRef.current);
        };
    }, [loadVersions, onClose]);

    const refreshActiveHtml = useCallback(async () => {
        try {
            const res = await leadpagesAPI.getLeadpageHtml(page.id);
            setIframeHtml(res.data);
            setViewingId(null);
        } catch { /* keep current preview */ }
    }, [page.id]);

    useEffect(() => {
        clearInterval(editPollRef.current);
        if (editing) {
            editPollRef.current = setInterval(async () => {
                try {
                    const res = await leadpagesAPI.getLeadpage(page.id);
                    if (!res.data.edit_in_progress) {
                        clearInterval(editPollRef.current);
                        setEditing(false);
                        onPageUpdate(res.data);
                        if (res.data.status_message) {
                            setEditError(res.data.status_message);
                        } else {
                            await Promise.all([refreshActiveHtml(), loadVersions()]);
                        }
                    }
                } catch {
                    clearInterval(editPollRef.current);
                    setEditing(false);
                }
            }, 4000);
        }
        return () => clearInterval(editPollRef.current);
    }, [editing, page.id, onPageUpdate, refreshActiveHtml, loadVersions]);

    const submitEdit = async (e) => {
        e.preventDefault();
        if (!editText.trim() || editing) return;
        setEditError(null);
        try {
            await leadpagesAPI.editLeadpage(page.id, editText.trim());
            setEditText('');
            setEditing(true);
        } catch (err) {
            setEditError(err.response?.data?.detail || 'Failed to start the edit');
        }
    };

    const viewVersion = async (version) => {
        if (version.is_active) {
            await refreshActiveHtml();
            return;
        }
        try {
            const res = await leadpagesAPI.getVersionHtml(page.id, version.id);
            setIframeHtml(res.data);
            setViewingId(version.id);
        } catch { /* keep current preview */ }
    };

    const rollback = async (version) => {
        try {
            const res = await leadpagesAPI.activateVersion(page.id, version.id);
            onPageUpdate(res.data);
            await Promise.all([refreshActiveHtml(), loadVersions()]);
        } catch (err) {
            setEditError(err.response?.data?.detail || 'Rollback failed');
        }
    };

    return (
        <div className="lp-modal-overlay" onClick={onClose}>
            <div className="lp-modal lp-modal-wide" onClick={(e) => e.stopPropagation()}>
                <div className="lp-modal-bar">
                    <strong className="lp-modal-title">{formatLabel(page.format_key)}</strong>
                    <div className="lp-device-toggle">
                        <button
                            className={device === 'desktop' ? 'active' : ''}
                            onClick={() => setDevice('desktop')}
                        >
                            🖥️ Desktop
                        </button>
                        <button
                            className={device === 'mobile' ? 'active' : ''}
                            onClick={() => setDevice('mobile')}
                        >
                            📱 Mobile
                        </button>
                    </div>
                    <div className="lp-modal-actions">
                        {onToggleTake && page.status !== 'exported' && (
                            <button
                                className={page.status === 'selected' ? 'btn-secondary lp-taken' : 'btn-primary'}
                                onClick={() => onToggleTake(page)}
                            >
                                {page.status === 'selected' ? '✓ Taken' : 'Take it'}
                            </button>
                        )}
                        <button className="lp-modal-close" onClick={onClose}>✕</button>
                    </div>
                </div>
                <div className="lp-modal-body">
                    <div className={`lp-iframe-wrap ${device}`}>
                        {viewingId && (
                            <div className="lp-viewing-banner">
                                Viewing an older version — current page is different
                            </div>
                        )}
                        <iframe srcDoc={iframeHtml} title="Leadpage preview" sandbox="allow-scripts" />
                    </div>
                    <div className="lp-edit-panel">
                        <h4>Ask AI to change it</h4>
                        <form className="lp-edit-form" onSubmit={submitEdit}>
                            <textarea
                                value={editText}
                                onChange={(e) => setEditText(e.target.value)}
                                placeholder='e.g. "Make the headline about Elon and the button red"'
                                rows={3}
                                disabled={editing}
                            />
                            <button type="submit" className="btn-primary" disabled={editing || !editText.trim()}>
                                {editing ? 'Applying…' : 'Apply change'}
                            </button>
                        </form>
                        {editing && (
                            <div className="lp-editing-note">
                                <span className="lp-spinner lp-spinner-sm" /> AI is editing the page…
                            </div>
                        )}
                        {editError && <p className="lp-error">{editError}</p>}

                        <h4>History</h4>
                        <div className="lp-versions">
                            {[...versions].reverse().map((v) => (
                                <div
                                    key={v.id}
                                    className={`lp-version-row ${v.is_active ? 'active' : ''} ${viewingId === v.id ? 'viewing' : ''}`}
                                    onClick={() => viewVersion(v)}
                                >
                                    <div className="lp-version-main">
                                        <strong>v{v.version_number}</strong>
                                        <span className="lp-version-text">
                                            {v.request_text || 'Original generation'}
                                        </span>
                                    </div>
                                    <div className="lp-version-side">
                                        {v.is_active ? (
                                            <span className="lp-group-badge lp-taken-badge">current</span>
                                        ) : (
                                            <button
                                                className="lp-rollback-btn"
                                                onClick={(e) => { e.stopPropagation(); rollback(v); }}
                                                disabled={editing}
                                            >
                                                Make current
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default LeadpagePreviewModal;
